import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faClock,
  faCircleCheck,
  faTriangleExclamation,
  faGlobe,
  faRotateRight,
  faUserGraduate,
} from '@fortawesome/free-solid-svg-icons';
import './Adminsettings.css';

/* =========================================================
   Admin › Settings — time zones (Settings table).
   Row 1 = academy time (Lebanon). Every class time, reminder and
   join alert uses it, so it must change when Lebanon changes its clocks.
   ========================================================= */
const API = process.env.REACT_APP_API_BASE_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('userData')}` } });

// 3 -> "UTC+3", 5.5 -> "UTC+5:30", -4 -> "UTC−4"
const fmtUtc = (h) => {
  if (h === null || h === undefined || h === '' || Number.isNaN(Number(h))) return '—';
  const n = Number(h);
  const sign = n < 0 ? '−' : '+';
  const abs = Math.abs(n);
  const hours = Math.floor(abs);
  const mins = Math.round((abs - hours) * 60);
  return `UTC${sign}${hours}${mins ? `:${String(mins).padStart(2, '0')}` : ''}`;
};

const AdminSettings = () => {
  const [rows, setRows] = useState([]);
  const [drafts, setDrafts] = useState({}); // id -> typed value
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(null); // id being saved
  const [toast, setToast] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/api/admin/settings`, auth());
      const list = Array.isArray(res.data?.data) ? res.data.data : [];
      setRows(list);
      setDrafts(Object.fromEntries(list.map((r) => [r.Id, String(r.TimeHours ?? '')])));
    } catch {
      setError("Couldn't load the settings. Please refresh.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(''), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  const save = async (row, value) => {
    const hours = Number(value);
    if (!Number.isFinite(hours) || hours < -12 || hours > 14) {
      setToast('Hours must be between -12 and 14 (e.g. 3, 2 or 5.5).');
      return;
    }
    setSaving(row.Id);
    try {
      await axios.put(`${API}/api/admin/settings/${row.Id}`, { TimeHours: hours }, auth());
      setRows((list) =>
        list.map((r) =>
          r.Id === row.Id
            ? { ...r, TimeHours: hours, Mismatch: r.ActualHours !== null && r.ActualHours !== hours }
            : r
        )
      );
      setDrafts((d) => ({ ...d, [row.Id]: String(hours) }));
      setToast(`${row.IsAcademy ? 'Academy time' : row.Timezone || 'Time zone'} saved: ${fmtUtc(hours)}`);
    } catch (err) {
      setToast(err.response?.data?.error || "Couldn't save. Please try again.");
    } finally {
      setSaving(null);
    }
  };

  const academy = rows.find((r) => r.IsAcademy);
  const others = rows.filter((r) => !r.IsAcademy);
  const othersOff = others.filter((r) => r.Mismatch).length;

  const editor = (row, big = false) => {
    const draft = drafts[row.Id] ?? '';
    const changed = draft !== '' && Number(draft) !== Number(row.TimeHours);
    return (
      <div className={`rmast-edit ${big ? 'is-big' : ''}`}>
        <label className="rmast-input">
          <span>UTC</span>
          <input
            type="number"
            step="0.25"
            min="-12"
            max="14"
            inputMode="decimal"
            value={draft}
            onChange={(e) => setDrafts((d) => ({ ...d, [row.Id]: e.target.value }))}
            aria-label={`Hours for ${row.IsAcademy ? 'academy time' : row.Timezone || 'time zone'}`}
          />
        </label>
        <button type="button" className="rmast-btn" disabled={!changed || saving === row.Id} onClick={() => save(row, draft)}>
          {saving === row.Id ? 'Saving…' : 'Save'}
        </button>
        {row.Mismatch && (
          <button
            type="button"
            className="rmast-btn rmast-btn--ghost"
            disabled={saving === row.Id}
            onClick={() => save(row, row.ActualHours)}
          >
            Use {fmtUtc(row.ActualHours)}
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="rmast">
      <header className="rmast-header">
        <div>
          <h1>Settings</h1>
          <p>Time zones used for class times, reminders and join alerts.</p>
        </div>
        <button type="button" className="rmast-btn rmast-btn--ghost" onClick={load} disabled={loading}>
          <FontAwesomeIcon icon={faRotateRight} /> Refresh
        </button>
      </header>

      {error && <p className="rmast-error">{error}</p>}

      {loading && !rows.length && (
        <div className="rmast-skel" aria-hidden="true">
          <span />
          <span />
          <span />
        </div>
      )}

      {academy && (
        <section className={`rmast-academy ${academy.Mismatch ? 'is-off' : 'is-ok'}`}>
          <div className="rmast-academy-top">
            <span className="rmast-academy-icon" aria-hidden="true">
              <FontAwesomeIcon icon={faClock} />
            </span>
            <div className="rmast-academy-text">
              <span className="rmast-eyebrow">Academy time · Lebanon</span>
              <strong>{fmtUtc(academy.TimeHours)}</strong>
              <p>
                Every class time on the website uses this. Change it when Lebanon changes its clocks
                (usually the last Sunday of March and of October).
              </p>
            </div>
            <span className={`rmast-pill ${academy.Mismatch ? 'tone-red' : 'tone-green'}`}>
              <FontAwesomeIcon icon={academy.Mismatch ? faTriangleExclamation : faCircleCheck} />
              {academy.ActualHours === null
                ? 'Check manually'
                : academy.Mismatch
                ? `Lebanon is ${fmtUtc(academy.ActualHours)} today`
                : 'Matches Lebanon today'}
            </span>
          </div>
          {editor(academy, true)}
        </section>
      )}

      {others.length > 0 && (
        <section className="rmast-card">
          <div className="rmast-card-head">
            <h2>
              <FontAwesomeIcon icon={faGlobe} /> Students' time zones
            </h2>
            {othersOff > 0 ? (
              <span className="rmast-pill tone-amber">
                <FontAwesomeIcon icon={faTriangleExclamation} /> {othersOff} to check
              </span>
            ) : (
              <span className="rmast-pill tone-green">
                <FontAwesomeIcon icon={faCircleCheck} /> All match today
              </span>
            )}
          </div>
          <p className="rmast-note">Used to show class times to students living abroad.</p>

          <ul className="rmast-list">
            {others.map((r) => (
              <li key={r.Id} className={r.Mismatch ? 'is-off' : ''}>
                <div className="rmast-zone">
                  <strong>{r.Timezone || `Time zone #${r.Id}`}</strong>
                  <span>
                    <FontAwesomeIcon icon={faUserGraduate} /> {r.Students} {r.Students === 1 ? 'student' : 'students'}
                    {r.ActualHours !== null && (
                      <>
                        {' · '}
                        <span className={r.Mismatch ? 'is-warn' : ''}>today {fmtUtc(r.ActualHours)}</span>
                      </>
                    )}
                  </span>
                </div>
                {editor(r)}
              </li>
            ))}
          </ul>
        </section>
      )}

      {toast && (
        <div className="rmast-toast" role="status">
          {toast}
        </div>
      )}
    </div>
  );
};

export default AdminSettings;